import React from 'react'
import _ from 'lodash'
import PropTypes from 'prop-types'
import { ProgressBranch } from 'camino-react'

import { cloudhelpPost, cloudhelpGet } from 'Utilities/CloudHelp'
import QueueAssignee from 'Components/Tickets/Form/Sections/QueueAssignee'
import { humanize } from 'Utilities/StringManipulator'
import Alert from 'Components/Alert'
import ErrorList from 'Components/ErrorList'
import TicketCreateFormSectionsTicketType from 'Components/Tickets/Form/Sections/TicketType'
import TicketCreateFormSectionsService from 'Components/Tickets/Form/Sections/Service'
import TicketCreateFormSectionsContacts from 'Components/Tickets/Form/Sections/Contacts'
import TicketCreateFormSectionsUserManagement from 'Components/Tickets/Form/Sections/UserManagement'

import BulkConfirmationTickets from './BulkConfirmationTickets'
import Confirmation from './Confirmation'

const toErrorMessage = (field, error) => {
  if (error === "can't be blank" || error === 'must exist') {
    return 'Please fill in the required fields'
  }

  const fieldName = humanize(field)

  return `${fieldName} ${error}`
}

const regionServiceCategories = (regionServiceCategoryIds, serviceCategories, regionId) => {
  let regionToScope = regionId
  if (regionId === 'ca') {
    regionToScope = 7
  }

  const filteredRegionServiceCategoryIds = _(regionServiceCategoryIds).filter(['region_id', regionToScope]).map('service_category_id').value()

  let scopedServiceCategories = _.filter(
    serviceCategories,
    category => _.includes(filteredRegionServiceCategoryIds, category.id)
  )

  if (_.isEmpty(scopedServiceCategories)) {
    scopedServiceCategories = _.filter(serviceCategories, ['region_id', regionId])
  }

  if (_.isEmpty(scopedServiceCategories)) {
    scopedServiceCategories = serviceCategories
  }

  return scopedServiceCategories
}

const filterTicketTypes = (ticketTypes, regionId, clientId) => {
  if (!_.isNil(clientId)) {
    const ticketTypesClientIds = _(ticketTypes).map('ownerable_id').compact().value()

    if (_.includes(ticketTypesClientIds, clientId)) {
      return _.filter(ticketTypes, ['ownerable_id', clientId])
    }
  }

  let scopedTicketTypes = _.filter(ticketTypes, ['ownerable_id', regionId])

  if (regionId === 'ca') {
    scopedTicketTypes = _.filter(ticketTypes, ['ownerable_id', 7])
  }

  if (_.isEmpty(scopedTicketTypes)) {
    scopedTicketTypes = _.filter(ticketTypes, ['ownerable_id', null])
  }

  return scopedTicketTypes
}

class TicketCreateForm extends React.Component {
  constructor (props) {
    super(props)

    const { current_user, ticket, clients } = props
    const { client_id, client_contact_id } = ticket || {}
    current_user.isGuestUser = current_user.guest_user
    current_user.isCalligoEmployee = current_user.calligo_employee

    const clientsWithInitialProperty = Object.assign([], [Object.assign({}, {})])
    const sortedClients = _.sortBy(_.map(clients, ({
      id: value,
      short_name: label,
      region_id,
      users,
      key_client: keyClient,
      client_status: clientStatus,
      migrated_to_halo: migratedToHalo
    }) => ({
      value,
      label,
      region_id,
      users,
      disabled: false,
      keyClient,
      clientStatus,
      migratedToHalo
    })), 'label')

    this.state = {
      currentModalSection: _.filter(
        ['Contacts', 'TicketType', 'Service', 'UserManagement', 'Queues'],
        section => !_.includes(props.omittedTabs, section)
      )[0] || 'Contacts',
      previousStages: [],
      clients: sortedClients,
      ticket_type_id: (ticket || {}).ticket_type_id,
      current_ticket_type: null,
      service_category_id: (ticket || {}).service_category_id,
      sub_category_id: (ticket || {}).sub_category_id,
      primary_contact_email: current_user.email_address,
      preferred_contact_method: '',
      secondary_contact_email: '',
      contact_number: null,
      creationSource: ticket.creation_source || 'portal',
      client_name: '',
      title: null,
      description: null,
      custom_field_values: null,
      submitButtonText: 'Create Ticket',
      hasErrors: false,
      errorText: [],
      fieldsWithErrors: [],
      ticket: null,
      users: [],
      tickets: [],
      customer_contract_id: null,
      service_bundle_id: null,
      client_id,
      client_contact_id: client_contact_id || current_user.id,
      serviceCategories: regionServiceCategories(
        props.regionServiceCategoryIds,
        props.allServiceCategories,
        props.regionId || 1
      ),
      assignee_user_id: null,
      assign_to_current_user: false,
      queue_id: (ticket || {}).queue_id,
      regionMetadata: {},
      regionId: props.regionId || 1,
      ticketTypes: filterTicketTypes(props.ticketTypes, props.regionId, _.get(current_user, 'client.id')),
      bulkCreation: false,
      clientsProperties: clientsWithInitialProperty,
      disabled_contact_notifications: false,
      security_issue: false
    }
  }

  attachHandlers = clientsProperties => (
    _.map(clientsProperties, (editedClient, index) => Object.assign({}, editedClient, {
      remove: () => this.removeEditedClient(index),
      setFieldValue: (option, value) => this.setChoosenField(index, option, value)
    }))
  )

  setChoosenField = (indexToSet, option, value) => {
    this.setState((prevState) => {
      const { clientsProperties } = prevState

      const editedClient = clientsProperties[indexToSet]
      const newClientValue = Object.assign({}, editedClient, { [option]: value })
      const newEditedClients = Object.assign([...clientsProperties], { [indexToSet]: newClientValue })

      return { clientsProperties: newEditedClients }
    })
  }

  removeEditedClient = (indexToRemove) => {
    this.setState((prevState) => {
      const { clientsProperties } = prevState
      const newClientProperites = _.filter(clientsProperties, (clientProperties, index) => indexToRemove !== index)
      const newClientsProperties = Object.assign([], newClientProperites)

      if (clientsProperties.length <= 2) {
        return { clientsProperties: newClientsProperties, bulkCreation: false }
      }
      return { clientsProperties: newClientsProperties }
    })
  }

  setNextModalSection = (nextModalSection = null) => {
    const { currentModalSection } = this.state
    let nextStage = nextModalSection

    const stages = _.map(
      this.modalSections,
      (section, index) => ({ index, name: section.name })
    )

    if (_.isNull(nextModalSection)) {
      const indexOfCurrentStage = _.find(stages, s => currentModalSection === s.name).index
      nextStage = stages[indexOfCurrentStage + 1].name
    }

    this.setState({
      currentModalSection: nextStage
    })
  }

  setFieldValue = (fieldName, value) => {
    this.setState({ [fieldName]: value })
  }

  setFieldErrorClasses = (field) => {
    const { fieldsWithErrors } = this.state

    if (_.includes(fieldsWithErrors, field)) {
      return ('error')
    }

    return ''
  }

  handleAssignToMe = (value) => {
    this.setFieldValue('assign_to_current_user', value)
  }

  submit = () => {
    this.setState((prevState) => {
      const { ticket_type_id } = prevState
      const { ticketTypes } = this.props

      const newState = Object.assign({}, {
        submitButtonText: 'Creating Ticket . . .',
        ticket_type_id: ticket_type_id || (ticketTypes.length === 1 ? ticketTypes[0].id : null)
      })

      return { newState }
    })

    const {
      ticket_type_id: ticketTypeId,
      service_category_id,
      sub_category_id,
      priority_id,
      primary_contact_email,
      contact_number,
      preferred_contact_method,
      secondary_contact_email,
      title,
      description,
      client_contact_id,
      primary_contact_name,
      client_name,
      client_id,
      assign_to_current_user,
      service_id,
      service_type,
      customer_contract_id,
      queue_id,
      regionId,
      regionMetadata,
      ticketTypes,
      bulkCreation,
      clientsProperties,
      assignee_user_id,
      disabled_contact_notifications,
      creationSource,
      security_issue
    } = this.state

    const ticket_type_id = ticketTypeId || (ticketTypes.length === 1 ? ticketTypes[0].id : null)
    const { paths, current_user } = this.props
    const { ticketsPath, bulkCreactionPath } = paths

    let regionIdToPost = regionId
    // let creationSourceForRegion = 'website_rotw'

    if (regionId === 'ca') {
      regionIdToPost = 2
      // creationSourceForRegion = 'website_canada'
    }

    // const creationSource = window === window.top ? 'portal' : creationSourceForRegion

    if (bulkCreation) {
      _.forEach(clientsProperties, (clientProperties) => {
        const selectedClient = _.find(this.state.clients, { value: clientProperties.client_id })
      })

      const postData = {
        ticket: {
          ticket_type_id,
          service_category_id,
          sub_category_id,
          title,
          description,
          region_id: regionIdToPost,
          region_metadata: regionMetadata,
          creation_source: creationSource,
          assignee_user_id,
          priority_id,
          queue_id,
        },
        clients_properties: clientsProperties
      }

      cloudhelpPost(bulkCreactionPath, postData)
        .then((response) => {
          const { tickets } = response.data.tickets
          this.setState({
            tickets,
            submitButtonText: 'Created'
          })

          if (window === window.top) {
            // not in frame, redirect
            window.location.href = response.data.redirect_url
          }
        })
        .catch((error) => {
          const { errors } = error.response.data
          const errorText = (error.response.status === 500 || error.response.status === 404) ? ['There was an issue creating your ticket.'] : _.uniq(
            _.map(
              errors[0],
              (msg, field) => toErrorMessage(field, msg)
            )
          )

          this.setState({
            hasErrors: true,
            fieldsWithErrors: _.keys(errors[0]),
            submitButtonText: 'Create',
            errorText
          })
        })
    } else {
      const clientProperties = _.head(clientsProperties)
      const client_contact_is_selected = current_user.calligo_employee && !_.isNil(clientProperties.client_contact_id)
      const selectedClient = current_user.calligo_employee
        ? _.find(this.state.clients, { value: clientProperties.client_id })
        : _.find(this.state.clients, { value: client_id }) || current_user.client

      if (selectedClient?.migratedToHalo || selectedClient?.migrated_to_halo) {
        console.log('Client is migrated to Halo')
      }

      const postData = {
        ticket: {
          primary_contact_email: current_user.calligo_employee ? clientProperties.primary_contact_email : primary_contact_email,
          primary_contact_name,
          client_name,
          contact_number,
          preferred_contact_method,
          ticket_type_id,
          service_category_id,
          sub_category_id,
          title,
          description,
          queue_id,
          priority_id,
          region_id: regionIdToPost,
          region_metadata: regionMetadata,
          creation_source: creationSource,
          assignee_user_id: assign_to_current_user ? current_user.id : assignee_user_id,
          secondary_contact_email: current_user.calligo_employee ? clientProperties.secondary_contact_email : secondary_contact_email,
          client_contact_id: client_contact_is_selected ? clientProperties.client_contact_id : client_contact_id,
          client_id: current_user.calligo_employee ? clientProperties.client_id : client_id,
          service_id,
          service_type,
          customer_contract_id,
          disabled_contact_notifications,
          security_issue
        }
      }

      cloudhelpPost(ticketsPath, postData)
        .then((response) => {
          const { ticket } = response.data

          this.setState({
            ticket,
            submitButtonText: 'Created'
          })

          if (window === window.top && !_.includes(window.location.href, 'dispatcher')) {
            // not in frame, redirect
            window.location.href = response.data.redirect_url
          }
        })
        .catch((error) => {
          const { response: errorResponse } = error
          const errors = _.get(errorResponse, 'data.errors') || []
          const errorText = errorResponse.status === (500 || 404) ? ['There was an issue creating your ticket.'] : _.uniq(
            _.map(
              errors[0],
              (msg, field) => toErrorMessage(field, msg)
            )
          )

          this.setState({
            hasErrors: true,
            fieldsWithErrors: _.keys(errors[0]),
            submitButtonText: 'Create',
            errorText
          })
        })
    }
  }

  isCurrentModalSection = (modalSection) => {
    const { currentModalSection } = this.state

    return currentModalSection === modalSection
  }

  statusOfForm = (stage) => {
    const { currentModalSection } = this.state
    const stages = _.map(
      this.modalSections,
      (section, index) => ({ index, name: section.name })
    )
    const indexOfCurrentStage = _.get(
      _.find(stages, s => currentModalSection === s.name),
      'index', 0
    )
    const possibleStage = _.find(stages, s => stage === s.name)

    if (possibleStage.index === indexOfCurrentStage) { return ['current'] }
    if (possibleStage.index === (indexOfCurrentStage - 1)) { return ['previous'] }
    if (possibleStage.index === (indexOfCurrentStage + 1)) { return ['next'] }
    if (possibleStage.index < indexOfCurrentStage) { return ['past'] }
    return ['future']
  }

  handleSelectChange = (fieldName, selectedOption = null) => {
    if (_.isNil(selectedOption)) {
      this.setState({ [fieldName]: null })
    } else {
      this.setState({ [fieldName]: selectedOption.value })
    }
  }

  handleRegionChange = (regionId) => {
    const {
      regionServiceCategoryIds,
      allServiceCategories,
      ticketTypes,
      current_user: {
        client
      }
    } = this.props

    const scopedServiceCategories = regionServiceCategories(
      regionServiceCategoryIds,
      allServiceCategories,
      regionId
    )

    this.setState({
      regionId,
      allServiceCategories: scopedServiceCategories || allServiceCategories,
      ticketTypes: filterTicketTypes(ticketTypes, regionId, client.id) || ticketTypes
    })
  }

  policy = (...args) => {
    const { policies } = this.props

    return _.get(policies, args)
  }

  refreshContacts = (clientId) => {
    cloudhelpGet(`/api/clients/${clientId}/users`)
      .then((response) => {
        const { data } = response
        addClientContactListToClient(clientId, data)
        this.setState({ clientContactsLoading: false })
      })
  }

  addClientHandler = () => {
    this.setState({ bulkCreation: true })
    this.setState((prevState) => {
      const { clientsProperties } = prevState
      const newClients = [...clientsProperties, Object.assign({}, {})]

      return { clientsProperties: newClients }
    })
  }

  addClientContactListToClient = (clientId, clientContactList) => {
    this.setState((prevState) => {
      const { clients } = prevState
      const list = Object.assign(_.find(clients, { value: clientId }), { users: clientContactList })
      const clientUsers = Object.assign([...clients], { [_.findIndex(clients, { value: clientId })]: list })

      return { clients: clientUsers }
    })
  }

  render () {
    const {
      policy,
      setFieldValue,
      setNextModalSection,
      isCurrentModalSection,
      submit,
      setFieldErrorClasses,
      handleSelectChange,
      refreshContacts,
      handleRegionChange,
      attachHandlers,
      addClientHandler,
      handleAssignToMe,
      addClientContactListToClient
    } = this

    const {
      errorText,
      hasErrors,
      submitButtonText,
      clients,
      client_id,
      ticket_type_id,
      customer_contract_id,
      service_bundle_id,
      service_category_id,
      sub_category_id,
      priority_id,
      preferred_contact_method,
      title,
      description,
      ticket,
      tickets,
      secondary_contact_email,
      primary_contact_name,
      primary_contact_email,
      client_name,
      contact_number,
      serviceCategories,
      assign_to_current_user,
      queue_id,
      chosenLocation,
      regionMetadata,
      ticketTypes,
      allServiceCategories,
      service_id,
      service_type,
      users,
      creatingContact,
      clientsProperties,
      bulkCreation,
      creatingContactForClient,
      assignee_user_id,
      currentModalSection,
      disabled_contact_notifications,
      security_issue
    } = this.state

    const {
      paths,
      current_user: currentUser,
      locations,
      omittedTabs,
      ticket: propsTicket,
      queuesWithAssignees,
      priorities
    } = this.props

    const clientsPropertiesWithHandlers = attachHandlers(clientsProperties)

    if (ticket) {
      return <Confirmation ticket={ticket} />
    }

    if (!_.isEmpty(tickets)) {
      return <BulkConfirmationTickets tickets={tickets} />
    }

    const currentUserClientMigratedToHalo = !currentUser.isCalligoEmployee
      && _.find(clients, { value: currentUser.client?.id })?.migratedToHalo

    if (currentUserClientMigratedToHalo) {
      return (
        <div>
          <Alert alertType="notice" showAlert>
            Please note that your company has now been migrated to the new service desk system.
            Viaje is available for historical ticket management only.
            Please logon to the new support portal at{' '}
            <a href="https://portal.calligo.io">https://portal.calligo.io</a>
            {' '}to raise any new tickets.
          </Alert>
        </div>
      )
    }

    const selectedCalligoClientMigratedToHalo = currentUser.isCalligoEmployee
      && clientsProperties.some(cp => cp.client_id && _.find(clients, { value: cp.client_id })?.migratedToHalo)

    const modalSection = (name, nodeLabel = null) => ({
      sectionComponent: name,
      node: {
        title: nodeLabel || name,
        onClick: () => { this.setNextModalSection(name) },
        status: this.statusOfForm(name)
      }
    })

    // The below determines what sections appear on the form as well as the order
    const userManagementCategoryId = _.get(_.find(serviceCategories, ['name', 'User Management']), 'id')
    const sections = [
      { name: 'Contacts', show: true },
      { name: 'TicketType', show: (ticketTypes.length >= 1 || _.has(propsTicket, 'ticket_type_id')), nodeLabel: 'Ticket type' },
      { name: 'Service', show: currentModalSection !== 'UserManagement' },
      { name: 'UserManagement', show: currentModalSection === 'UserManagement', nodeLabel: 'User Management' },
      { name: 'Queues', show: currentUser.calligo_employee }
    ]

    this.modalSections = _.filter(sections, section => section.show && !_.includes(omittedTabs, section.name))
    const modalSectionsOptions = _.map(this.modalSections, section => modalSection(section.name, section.nodeLabel))
    const currentTicketType = ticketTypes.length === 1 ? ticketTypes[0] : _.find(ticketTypes, ['id', ticket_type_id])
    const ticketTypeSelectedCategories = currentTicketType && _.get(currentTicketType, 'service_categories', [])
    const ticketTypeAndNoAssociatedCategories = (!_.isNil(ticket_type_id) && _.isEmpty(ticketTypeSelectedCategories))
    const currentServiceCategories = ticketTypeAndNoAssociatedCategories ? this.props.allServiceCategories : ticketTypeSelectedCategories

    const contactsSectionProps = {
      setNextModalSection,
      paths,
      policy,
      handleSelectChange,
      handleRegionChange,
      chosenLocation,
      locations,
      setFieldValue,
      currentUser,
      setFieldErrorClasses,
      regionMetadata,
      clients,
      users,
      addClientContactListToClient,
      primary_contact_name,
      primary_contact_email,
      client_name,
      preferred_contact_method,
      secondary_contact_email,
      contact_number,
      assign_to_current_user,
      queue_id,
      addClientHandler,
      clientsPropertiesWithHandlers,
      bulkCreation,
      creatingContact,
      creatingContactForClient,
      queuesWithAssignees,
      handleAssignToMe,
      disabled_contact_notifications,
      security_issue
    }

    return (
      <div>
        {selectedCalligoClientMigratedToHalo && (
          <Alert alertType="warning" showAlert>
            <span>
              This customer has now been migrated to Halo. Viaje should only be used for historical
              ticket management. Please log all new tickets via the Halo portal at{' '}
              <a href="https://halo.calligo.io" target="_blank" rel="noopener noreferrer">
                https://halo.calligo.io
              </a>
            </span>
          </Alert>
        )}
        <form>
          <Alert alertType="error" showAlert={hasErrors}>
            <ErrorList errors={errorText} />
          </Alert>

          <div style={selectedCalligoClientMigratedToHalo ? { pointerEvents: 'none', opacity: 0.5 } : {}}>
            <ProgressBranch nodes={_.map(modalSectionsOptions, 'node')} />
          </div>

          <div style={selectedCalligoClientMigratedToHalo ? { pointerEvents: 'none', opacity: 0.5 } : {}}>
            <TicketCreateFormSectionsTicketType {...{
              hidden: !isCurrentModalSection('TicketType'),
              setNextModalSection: () => { setNextModalSection('Service') },
              currentUser,
              ticket_type_id,
              ticketTypes,
              setFieldValue,
              serviceCategories,
              setFieldErrorClasses
            }}
            />

            <TicketCreateFormSectionsService {...{
              hidden: !isCurrentModalSection('Service'),
              setNextModalSection,
              currentUser,
              ticketType: _.find(ticketTypes, ['id', ticket_type_id]) || {},
              ticket_type_id,
              handleSelectChange,
              service_category_id,
              ticket_type_id,
              sub_category_id,
              userManagementCategoryId,
              priority_id,
              serviceCategories: currentServiceCategories,
              setFieldErrorClasses,
              regionMetadata,
              allServiceCategories,
              clientId: client_id,
              customer_contract_id,
              service_id,
              service_type,
              priorities,
              canSetPriority: currentUser.isCalligoEmployee || _.get(currentUser, 'client.id') == 1179,
              title,
              description,
              setFieldValue,
              submitAction: !currentUser.calligo_employee ? () => submit() : '',
              submitButtonText,
            }}
            />

            <TicketCreateFormSectionsUserManagement {...{
              hidden: !isCurrentModalSection('UserManagement'),
              setNextModalSection,
              ticketType: _.find(ticketTypes, ['id', ticket_type_id]) || {},
              currentUser,
              sub_category_id,
              serviceCategories,
              setFieldValue,
              serviceCategoryId: service_category_id,
              userManagementCategoryId,
              title,
              regionMetadata,
              setFieldErrorClasses
            }}
            />
          </div>

          <TicketCreateFormSectionsContacts {...{
            ...contactsSectionProps,
            hidden: !creatingContact && !isCurrentModalSection('Contacts'),
            disableNext: selectedCalligoClientMigratedToHalo
          }} />

          <div style={selectedCalligoClientMigratedToHalo ? { pointerEvents: 'none', opacity: 0.5 } : {}}>
            {(currentUser.calligo_employee) && (
              <QueueAssignee {...{
                hidden: !isCurrentModalSection('Queues'),
                submitAction: () => submit(),
                submitButtonText,
                queuesWithAssignees,
                setFieldErrorClasses,
                setFieldValue,
                queue_id,
                assign_to_current_user,
                assignee_user_id
              }}
              />
            )}
          </div>
        </form>
      </div>
    )
  }
}

TicketCreateForm.propTypes = {
  current_user: PropTypes.shape({}),
  paths: PropTypes.shape({
    ticketsPath: PropTypes.string
  }).isRequired,
  policies: PropTypes.objectOf(PropTypes.object).isRequired,
  regionId: PropTypes.oneOf([PropTypes.string, PropTypes.number]),
  ticketTypes: PropTypes.arrayOf(PropTypes.object).isRequired
}

TicketCreateForm.defaultProps = {
  current_user: {},
  regionId: 1,
  omittedTabs: [],
  priorities: []
}

export default TicketCreateForm
